import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import MoodleService from '@/services/moodle';
import { useUserStore } from '@/stores/user';

// Standard Moodle create fields always required by the web service.
export const requiredCreateFields = ['username', 'password', 'firstname', 'lastname', 'email'];

// Email format check, mirroring the project-wide validator (src/use/validator.js).
const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export function isValidEmail(value) {
  return EMAIL_REGEX.test(String(value || '').trim());
}

// Moodle custom profile field shortnames (the `type` sent to the web service).
export const customFieldKeys = ['partner_name', 'partner_email', 'partner_nif', 'zs_profile'];

export const zsProfileOptions = [
  { label: 'comercial', value: 'comercial' },
  { label: 'Técnico', value: 'tech' },
  { label: 'Ambos', value: 'both' },
];

export function emptyCustomFields() {
  return { partner_name: '', partner_email: '', partner_nif: '', zs_profile: '' };
}

// Reads a custom profile field value from a Moodle user's `customfields` array by shortname.
export function customFieldValue(item, shortname) {
  const entry = (item?.customfields || []).find((field) => field?.shortname === shortname);
  return entry?.value || '';
}

// Builds the Moodle `customfields` payload (array of {type, value}) from a flat object, dropping empties.
export function buildCustomFields(values) {
  return customFieldKeys
    .map((key) => ({ type: key, value: String(values?.[key] ?? '').trim() }))
    .filter((entry) => entry.value !== '');
}

// Builds the `customfields` payload for the ZS Academy prefill form. The partner
// custom fields mirror the canonical top-level inputs (so they cannot drift from
// them); only `zs_profile` is an independent input.
export function buildPrefillCustomFields(form) {
  return buildCustomFields({
    partner_name: form.partnerName,
    partner_email: form.partnerEmail,
    partner_nif: form.partnerTax,
    zs_profile: form.zs_profile,
  });
}

export function getErrorMessage(err, fallback) {
  return err?.response?.data?.detail || err?.response?.data?.message || err?.message || fallback;
}

export function normalizeBooleanValue(value) {
  const normalized = String(value).trim().toLowerCase();
  if (['1', 'true', 'yes'].includes(normalized)) {
    return true;
  }
  if (['0', 'false', 'no'].includes(normalized)) {
    return false;
  }

  return null;
}

export function splitName(name) {
  const value = String(name || '').trim();
  if (!value) {
    return { firstName: '', lastName: '' };
  }

  const parts = value.split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: parts[0] };
  }

  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1],
  };
}

// Shared Moodle user logic used by both the users management view and the
// self-registration wizard. Encapsulates i18n, the user store and a Moodle
// service instance so both callers build the exact same payloads and run the
// same validations. Call from a component `setup()`.
export function useMoodleUser() {
  const { t } = useI18n();
  const userStore = useUserStore();
  const { user, partner, partnerInfo } = storeToRefs(userStore);
  const moodleService = new MoodleService();

  function buildPrefillDefaults() {
    const contactName = user.value?.user_name || partner.value?.nome_contacto || '';
    const splitContactName = splitName(contactName);
    return {
      partnerName: partner.value?.nome || user.value?.partner_name || '',
      partnerTax: partner.value?.nif || user.value?.partner_tax || '',
      partnerEmail: partner.value?.email || user.value?.partner_email || '',
      contactEmail: user.value?.email || '',
      // Moodle usernames are canonically lowercase; get_users_by_field rejects
      // values that differ from their cleaned (lowercased) form.
      username: (user.value?.login || user.value?.email || '').toLowerCase(),
      password: '',
      firstName: splitContactName.firstName,
      lastName: splitContactName.lastName,
      // `zs_profile` is the only independent custom field; the remaining partner
      // custom fields are derived from the canonical inputs at submit time
      // (see buildPrefillCustomFields). Defaults to 'comercial' for now.
      zs_profile: 'comercial',
    };
  }

  // Enforce Moodle's password policy client-side before submitting, so users get
  // immediate feedback instead of a server-side rejection. Returns the list of
  // unmet requirements (empty when the password is valid).
  function validatePassword(password) {
    const pw = String(password || '');
    const unmet = [];

    if (pw.length < 8) {
      unmet.push(t('moodle_users.password_rule_length'));
    }
    if (!/\d/.test(pw)) {
      unmet.push(t('moodle_users.password_rule_digit'));
    }
    if (!/[A-Z]/.test(pw)) {
      unmet.push(t('moodle_users.password_rule_upper'));
    }
    if (!/[a-z]/.test(pw)) {
      unmet.push(t('moodle_users.password_rule_lower'));
    }
    if (!/[^a-zA-Z0-9]/.test(pw)) {
      unmet.push(t('moodle_users.password_rule_special'));
    }

    return unmet;
  }

  function buildCreateUserPayloadFromFields(fields) {
    const payload = {};
    const seenKeys = new Set();

    for (const field of fields) {
      const key = String(field.key || '').trim();
      const value = String(field.value || '').trim();

      if (!key && !value) {
        continue;
      }

      if (!key) {
        return { error: t('moodle_users.validation_missing_field_name') };
      }

      if (seenKeys.has(key)) {
        return { error: t('moodle_users.validation_duplicate_field', { field: key }) };
      }
      seenKeys.add(key);

      if (key === 'suspended') {
        if (!value) {
          continue;
        }

        const normalizedBoolean = normalizeBooleanValue(value);
        if (normalizedBoolean === null) {
          return { error: t('moodle_users.validation_boolean') };
        }

        payload[key] = normalizedBoolean;
        continue;
      }

      payload[key] = value;
    }

    for (const requiredField of requiredCreateFields) {
      if (!String(payload[requiredField] || '').trim()) {
        return { error: t('moodle_users.validation_required', { field: requiredField }) };
      }
    }

    if (!isValidEmail(payload.email)) {
      return { error: t('moodle_users.validation_email') };
    }

    const passwordIssues = validatePassword(payload.password);
    if (passwordIssues.length) {
      return {
        error: `${t('moodle_users.validation_password_policy')} ${passwordIssues.join('; ')}`,
      };
    }

    return { payload };
  }

  // Builds the create-user payload from the ZS Academy prefill form shape.
  // Shared by the users view (prefill tab) and the self-registration wizard so
  // both produce an identical request.
  function buildPrefillPayload(form) {
    const fields = [
      { key: 'institution', value: form.partnerName },
      { key: 'idnumber', value: form.partnerTax },
      { key: 'email', value: form.contactEmail },
      { key: 'username', value: form.username },
      { key: 'password', value: form.password },
      { key: 'firstname', value: form.firstName },
      { key: 'lastname', value: form.lastName },
    ];

    return buildCreateUserPayloadFromFields(fields);
  }

  async function checkExistingMoodleUser({ email, username }) {
    const cleanEmail = String(email || '').trim();
    if (cleanEmail) {
      try {
        const response = await moodleService.getUsersByField(userStore.token, 'email', cleanEmail);
        const list = response?.data ?? [];
        if (list.length > 0) {
          return {
            error: t('moodle_users.validation_existing_email', { value: cleanEmail }),
          };
        }
      } catch (err) {
        return {
          error: t('moodle_users.validation_check_error'),
        };
      }
    }

    const cleanUsername = String(username || '').trim().toLowerCase();
    if (cleanUsername) {
      try {
        const response = await moodleService.getUsersByField(userStore.token, 'username', cleanUsername);
        const list = response?.data ?? [];
        if (list.length > 0) {
          return {
            error: t('moodle_users.validation_existing_username', { value: cleanUsername }),
          };
        }
      } catch (err) {
        return {
          error: t('moodle_users.validation_check_error'),
        };
      }
    }

    return { error: null };
  }

  return {
    // store refs
    user,
    partner,
    partnerInfo,
    // service
    moodleService,
    // helpers
    buildPrefillDefaults,
    validatePassword,
    buildCreateUserPayloadFromFields,
    buildPrefillPayload,
    checkExistingMoodleUser,
  };
}
